"use client";

import { CommonCrawlRankForm } from "@marketing/common-crawl-rank-checker";
import { FAQSection } from "@marketing/shared/components/FAQSection";
import {
	BentoGrid,
	CtaPair,
	Section,
	SectionHeader,
} from "@marketing/shared/components/bento/Bento";
import { CollapsedTakeaways } from "@marketing/shared/components/bento/KeyTakeaways";
import { Reveal } from "@marketing/shared/components/bento/motion";
import {
	BarChart3Icon,
	LinkIcon,
	NetworkIcon,
	SearchIcon,
	TrophyIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

const STEPS = [
	{ icon: SearchIcon, key: "step1" },
	{ icon: NetworkIcon, key: "step2" },
	{ icon: TrophyIcon, key: "step3" },
	{ icon: BarChart3Icon, key: "step4" },
] as const;

const WHY = [
	{ icon: NetworkIcon, key: "training" },
	{ icon: LinkIcon, key: "earnedMedia" },
] as const;

export function CommonCrawlRankCheckerClient() {
	const t = useTranslations();

	const faqItems = Array.from({ length: 8 }, (_, i) => ({
		question: t(
			`marketing.commonCrawlRankChecker.faq.q${i + 1}.question` as any,
		),
		answer: t(
			`marketing.commonCrawlRankChecker.faq.q${i + 1}.answer` as any,
		),
	}));

	return (
		<>
			{/* Hero */}
			<Section className="pt-24 lg:pt-28">
				<div className="mx-auto max-w-3xl text-center">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
						<NetworkIcon className="size-4" />
						<span>
							{t("marketing.commonCrawlRankChecker.badge")}
						</span>
					</div>
					<h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
						{t("marketing.commonCrawlRankChecker.title")}
					</h1>
					<p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
						{t("marketing.commonCrawlRankChecker.subtitle")}
					</p>
					<div className="mx-auto mt-10 max-w-xl">
						<CommonCrawlRankForm />
					</div>
				</div>
			</Section>

			{/* Entity-dense primary descriptive section + key takeaways (AEO) */}
			<Section muted>
				<Reveal>
					<div className="mx-auto max-w-3xl">
						<h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
							Why Common Crawl authority feeds AI answers
						</h2>
						<div className="mt-4 space-y-3 text-lg text-muted-foreground">
							<p>
								Common Crawl is the open, petabyte-scale web
								archive that underpins much of the data used to
								train large language models. Its web graph ranks
								domains by Harmonic Centrality and PageRank —
								measures of how many authoritative sites link to
								yours. This tool looks a domain up in that graph
								and returns its rank, so you can gauge how
								visible your site is in the corpus AI systems
								learn from.
							</p>
							<p>
								Domains with high harmonic centrality —
								established publishers, reference sites, and
								category authorities — are over-represented in
								training data and more likely to be recalled
								when an assistant answers. A strong Common Crawl
								rank signals durable, link-earned authority, the
								same signal that separates a citation-worthy
								source from one AI overlooks.
							</p>
						</div>
						<CollapsedTakeaways
							className="mt-6"
							summary="Key takeaways"
							facts={[
								"Common Crawl is a primary training source for LLMs; rank in its web graph correlates with how often a domain surfaces in AI answers.",
								"Harmonic Centrality and PageRank measure link-earned authority, not on-page SEO — they reward being cited by other authoritative sites.",
								"High-authority domains are over-represented in training corpora, giving them a recall advantage in generated answers.",
								"Rank complements an AI Placement Value Score: authority in the graph plus AI accessibility is what makes a domain worth a placement.",
							]}
						/>
					</div>
				</Reveal>
			</Section>

			{/* How It Works */}
			<Section>
				<Reveal>
					<SectionHeader
						title={t(
							"marketing.commonCrawlRankChecker.howItWorks.title",
						)}
						lede={t(
							"marketing.commonCrawlRankChecker.howItWorks.subtitle",
						)}
					/>
				</Reveal>
				<BentoGrid className="mt-10 lg:grid-cols-4">
					{STEPS.map((step, i) => (
						<Reveal key={step.key} delay={i * 80}>
							<div className="h-full rounded-2xl border bg-card p-5 text-center">
								<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
									<step.icon
										className="size-6 text-primary"
										aria-hidden
									/>
								</div>
								<h3 className="mt-4 text-base font-bold">
									{t(
										`marketing.commonCrawlRankChecker.howItWorks.${step.key}.title` as any,
									)}
								</h3>
								<p className="mt-1.5 text-sm text-muted-foreground">
									{t(
										`marketing.commonCrawlRankChecker.howItWorks.${step.key}.description` as any,
									)}
								</p>
							</div>
						</Reveal>
					))}
				</BentoGrid>
			</Section>

			{/* Why It Matters */}
			<Section muted>
				<Reveal>
					<SectionHeader
						title={t(
							"marketing.commonCrawlRankChecker.whyItMatters.title",
						)}
						lede={t(
							"marketing.commonCrawlRankChecker.whyItMatters.subtitle",
						)}
					/>
				</Reveal>
				<BentoGrid className="mt-10 lg:grid-cols-2">
					{WHY.map((card, i) => (
						<Reveal key={card.key} delay={i * 80}>
							<div className="h-full rounded-2xl border bg-card p-6">
								<div className="mb-4 flex items-center gap-3">
									<card.icon
										className="size-6 text-primary"
										aria-hidden
									/>
									<h3 className="text-lg font-bold">
										{t(
											`marketing.commonCrawlRankChecker.whyItMatters.${card.key}.title` as any,
										)}
									</h3>
								</div>
								<p className="text-muted-foreground">
									{t(
										`marketing.commonCrawlRankChecker.whyItMatters.${card.key}.description` as any,
									)}
								</p>
							</div>
						</Reveal>
					))}
				</BentoGrid>
			</Section>

			{/* CTA */}
			<Section>
				<Reveal>
					<div className="mx-auto max-w-2xl rounded-2xl border bg-card p-10 text-center">
						<h2 className="text-3xl font-extrabold tracking-tight">
							{t("marketing.commonCrawlRankChecker.cta.title")}
						</h2>
						<p className="mx-auto mt-3 max-w-xl text-muted-foreground">
							{t("marketing.commonCrawlRankChecker.cta.subtitle")}
						</p>
						<CtaPair
							className="mt-6 justify-center"
							primary={t(
								"marketing.commonCrawlRankChecker.cta.button",
							)}
							primaryHref="/auth/signup"
						/>
					</div>
				</Reveal>
			</Section>

			{/* FAQ */}
			<FAQSection
				title={t("marketing.commonCrawlRankChecker.faq.title")}
				description=""
				items={faqItems}
			/>
		</>
	);
}
